import { GRADUATION_QUOTE_WEI } from '@/lib/launchpad/constants'
import type { LaunchState, SimPool } from '@/lib/launchpad/types'
import { optionalTokenSocials, type TokenSocials } from '@/lib/tokenSocials'
import { jsonToBigint } from '@/lib/launchpad/wei'

interface SerializedPool {
  quoteReserve: string
  tokenReserve: string
  k: string
}

export interface SerializedLaunch {
  id: string
  name: string
  ticker: string
  imageUrl: string
  socials?: TokenSocials
  creatorAddress: string
  contractAddress?: string
  tokenAddress?: string
  shareSlug?: string
  createdAtMs: number
  lastTradeAtMs?: number
  tradeFeeBps: number
  creatorShareBps: number
  platformShareBps: number
  virtualQuote: string
  virtualToken: string
  k: string
  realQuoteRaised: string
  graduationQuote?: string
  tokensSold: string
  creatorFeesAccrued: string
  platformFeesAccrued: string
  antiSnipeFeesAccrued: string
  status: LaunchState['status']
  simPool?: SerializedPool
  priceHistory?: { t: number, supply: number, price: number, phase?: 'curve' | 'pool' }[]
}

function serializePool(pool: SimPool): SerializedPool {
  return {
    quoteReserve: pool.quoteReserve.toString(),
    tokenReserve: pool.tokenReserve.toString(),
    k: pool.k.toString(),
  }
}

function deserializePool(raw: SerializedPool | undefined): SimPool | undefined {
  if (!raw)
    return undefined
  return {
    quoteReserve: jsonToBigint(raw.quoteReserve),
    tokenReserve: jsonToBigint(raw.tokenReserve),
    k: jsonToBigint(raw.k),
  }
}

export function serializeLaunch(launch: LaunchState): SerializedLaunch {
  return {
    id: launch.id,
    name: launch.name,
    ticker: launch.ticker,
    imageUrl: launch.imageUrl,
    socials: optionalTokenSocials(launch.socials),
    creatorAddress: launch.creatorAddress,
    contractAddress: launch.contractAddress,
    tokenAddress: launch.tokenAddress,
    shareSlug: launch.shareSlug,
    createdAtMs: launch.createdAtMs,
    lastTradeAtMs: launch.lastTradeAtMs ?? launch.createdAtMs,
    tradeFeeBps: launch.tradeFeeBps,
    creatorShareBps: launch.creatorShareBps,
    platformShareBps: launch.platformShareBps,
    virtualQuote: launch.virtualQuote.toString(),
    virtualToken: launch.virtualToken.toString(),
    k: launch.k.toString(),
    realQuoteRaised: launch.realQuoteRaised.toString(),
    graduationQuote: launch.graduationQuote.toString(),
    tokensSold: launch.tokensSold.toString(),
    creatorFeesAccrued: launch.creatorFeesAccrued.toString(),
    platformFeesAccrued: launch.platformFeesAccrued.toString(),
    antiSnipeFeesAccrued: launch.antiSnipeFeesAccrued.toString(),
    status: launch.status,
    simPool: launch.simPool ? serializePool(launch.simPool) : undefined,
    priceHistory: launch.priceHistory ?? [],
  }
}

export function deserializeLaunch(raw: SerializedLaunch): LaunchState {
  return {
    id: raw.id,
    name: raw.name,
    ticker: raw.ticker,
    imageUrl: raw.imageUrl ?? '',
    socials: optionalTokenSocials(raw.socials),
    creatorAddress: raw.creatorAddress,
    contractAddress: raw.contractAddress,
    tokenAddress: raw.tokenAddress,
    shareSlug: raw.shareSlug,
    createdAtMs: raw.createdAtMs,
    lastTradeAtMs: raw.lastTradeAtMs ?? raw.createdAtMs,
    tradeFeeBps: raw.tradeFeeBps,
    creatorShareBps: raw.creatorShareBps,
    platformShareBps: raw.platformShareBps,
    virtualQuote: jsonToBigint(raw.virtualQuote),
    virtualToken: jsonToBigint(raw.virtualToken),
    k: jsonToBigint(raw.k),
    realQuoteRaised: jsonToBigint(raw.realQuoteRaised),
    graduationQuote: raw.graduationQuote ? jsonToBigint(raw.graduationQuote) : GRADUATION_QUOTE_WEI,
    tokensSold: jsonToBigint(raw.tokensSold),
    creatorFeesAccrued: jsonToBigint(raw.creatorFeesAccrued),
    platformFeesAccrued: jsonToBigint(raw.platformFeesAccrued),
    antiSnipeFeesAccrued: jsonToBigint(raw.antiSnipeFeesAccrued),
    status: raw.status === 'graduated' ? 'graduated' : 'curve',
    simPool: deserializePool(raw.simPool),
    priceHistory: Array.isArray(raw.priceHistory) ? raw.priceHistory : [],
  }
}
